<template>
    <div v-if="loading" class="flex justify-center py-12">
        <ticto-loading />
    </div>

    <div v-else-if="review" class="space-y-6">
        <!-- Header -->
        <div class="flex items-center justify-between">
            <div class="flex items-center gap-4">
                <back-button :route="{ name: 'finnancial.chargebacks.reviews' }" />
                <div>
                    <div class="flex items-center gap-3">
                        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
                            Revisão #{{ review.id }}
                        </h2>
                        <span
                            class="inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset"
                            :class="statusClass"
                        >
                            {{ review.status === 'open' ? 'Aberto' : 'Fechado' }}
                        </span>
                    </div>
                    <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                        Aberto em {{ review.created_at }}
                        <span v-if="review.closed_at"> · Fechado em {{ review.closed_at }}</span>
                    </p>
                </div>
            </div>

            <button
                v-if="review.status === 'open'"
                @click="showCloseModal = true"
                class="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="closing"
            >
                <XCircleIcon class="h-4 w-4" />
                Encerrar Revisão
            </button>
        </div>

        <!-- Producer Info -->
        <div class="rounded-lg drop-shadow-sm overflow-hidden bg-white dark:bg-gray-800 p-5">
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-4">
                    <div class="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30">
                        <UserIcon class="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                        <a
                            :href="'/user/' + review.producer_id + '/audit/trackings'"
                            target="_blank"
                            class="text-lg font-semibold text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 hover:underline"
                        >
                            {{ review.producer_name || '-' }}
                        </a>
                        <p class="text-sm text-gray-500 dark:text-gray-400">
                            ID:
                            <a
                                :href="'/user/' + review.producer_id + '/audit/trackings'"
                                target="_blank"
                                class="text-indigo-500 hover:underline dark:text-indigo-400"
                            >{{ review.producer_id }}</a>
                            ·
                            <a
                                :href="'/user/' + review.producer_id + '/audit/trackings'"
                                target="_blank"
                                class="text-indigo-500 hover:underline dark:text-indigo-400"
                            >{{ review.email }}</a>
                        </p>
                    </div>
                </div>
                <div class="flex items-center gap-3">
                    <div v-if="review.audit_id" class="text-right">
                        <router-link
                            :to="{ name: 'audit.single', params: { audit_id: review.audit_id } }"
                            class="text-sm font-medium text-indigo-500 hover:underline dark:text-indigo-400"
                        >
                            Auditoria iniciada {{ review.audit_created_at }}
                        </router-link>
                    </div>
                    <button
                        v-else
                        @click="startAudit"
                        :disabled="startingAudit"
                        class="btn-primary-sm"
                    >
                        {{ startingAudit ? 'Iniciando...' : 'Iniciar Auditoria' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Stat Cards -->
        <div class="grid grid-cols-3 gap-4">
            <div class="min-w-0 rounded-lg drop-shadow-sm overflow-hidden bg-white dark:bg-gray-800">
                <div class="p-4">
                    <p class="mb-2 text-gray-400 dark:text-gray-400 uppercase font-bold text-sm">Vendas Autorizadas</p>
                    <p class="text-2xl font-semibold text-gray-900 dark:text-white">{{ review.total_authorized.toLocaleString('pt-BR') }}</p>
                    <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">{{ formatCurrency(review.total_authorized_amount) }}</p>
                </div>
            </div>
            <div class="min-w-0 rounded-lg drop-shadow-sm overflow-hidden bg-white dark:bg-gray-800">
                <div class="p-4">
                    <p class="mb-2 text-gray-400 dark:text-gray-400 uppercase font-bold text-sm">Chargebacks</p>
                    <p class="text-2xl font-semibold text-gray-900 dark:text-white">{{ review.total_chargeback.toLocaleString('pt-BR') }}</p>
                    <p class="text-xs text-red-500 dark:text-red-400 mt-1">{{ formatCurrency(review.total_chargeback_amount) }}</p>
                </div>
            </div>
            <div class="min-w-0 rounded-lg drop-shadow-sm overflow-hidden bg-white dark:bg-gray-800">
                <div class="p-4">
                    <p class="mb-2 text-gray-400 dark:text-gray-400 uppercase font-bold text-sm">Regra Atingida</p>
                    <span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/20 dark:text-red-400 dark:ring-red-500/30">
                        {{ review.rule }}
                    </span>
                </div>
            </div>
        </div>
        <div class="grid grid-cols-2 gap-4">
            <div class="min-w-0 rounded-lg drop-shadow-sm overflow-hidden bg-white dark:bg-gray-800">
                <div class="p-4">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="mb-1 text-gray-400 dark:text-gray-400 uppercase font-bold text-sm">Taxa TPN (quantidade)</p>
                            <p class="text-2xl font-semibold" :class="rateTextClass">
                                {{ review.pct_chargeback.toFixed(2).replace('.', ',') }}%
                            </p>
                        </div>
                        <div class="text-right">
                            <p class="text-xs text-gray-400 dark:text-gray-500">{{ review.total_chargeback }} CBs / {{ review.total_authorized }} vendas</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="min-w-0 rounded-lg drop-shadow-sm overflow-hidden bg-white dark:bg-gray-800">
                <div class="p-4">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="mb-1 text-gray-400 dark:text-gray-400 uppercase font-bold text-sm">Taxa TPV (valor R$)</p>
                            <p class="text-2xl font-semibold" :class="tpvTextClass">
                                {{ review.tpv_rate ? review.tpv_rate.toFixed(2).replace('.', ',') : '0,00' }}%
                            </p>
                        </div>
                        <div class="text-right">
                            <p class="text-xs text-gray-400 dark:text-gray-500">{{ formatCurrency(review.total_chargeback_amount) }} / {{ formatCurrency(review.total_authorized_amount) }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="grid grid-cols-4 gap-6">
            <!-- Main Content (3 cols) -->
            <div class="col-span-3 space-y-6">
                <!-- Progression Chart -->
                <div v-if="review.monthly_progression && review.monthly_progression.length > 0" class="rounded-lg drop-shadow-sm overflow-hidden bg-white dark:bg-gray-800 p-6">
                    <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Progressão Mensal</h3>
                    <highcharts :options="chartOptions"></highcharts>
                </div>

                <!-- Chargebacks Table -->
                <div v-if="cbTransactions.data && cbTransactions.data.length > 0" class="rounded-lg drop-shadow-sm overflow-hidden bg-white dark:bg-gray-800 p-6">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase">Chargebacks — Detalhamento</h3>
                        <span class="text-xs text-gray-400 dark:text-gray-500">
                            {{ cbTransactions.from }}–{{ cbTransactions.to }} de {{ cbTransactions.total }}
                        </span>
                    </div>
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead class="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Transação</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Pedido</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                    <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Valor</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
                                <tr v-for="tx in cbTransactions.data" :key="tx.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                    <td class="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{{ tx.created_at }}</td>
                                    <td class="px-4 py-3 text-sm font-mono text-indigo-600 dark:text-indigo-400">{{ tx.hash }}</td>
                                    <td class="px-4 py-3 text-sm font-mono text-gray-500 dark:text-gray-400">{{ tx.order_hash }}</td>
                                    <td class="px-4 py-3">
                                        <span
                                            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                            :class="{
                                                'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400': tx.status === 'chargeback',
                                                'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400': tx.status === 'disputed',
                                                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400': tx.status === 'chargeback_refunded',
                                            }"
                                        >
                                            {{ statusLabel(tx.status) }}
                                        </span>
                                    </td>
                                    <td class="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white whitespace-nowrap">{{ formatCurrency(tx.paid_amount) }}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr class="bg-gray-50 dark:bg-gray-700/30">
                                    <td colspan="4" class="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white text-right">Total geral</td>
                                    <td class="px-4 py-3 text-sm text-right font-bold text-red-600 dark:text-red-400">{{ formatCurrency(review.total_chargeback_amount) }}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    <!-- Pagination -->
                    <div v-if="cbTransactions.last_page > 1" class="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                            Página {{ cbTransactions.current_page }} de {{ cbTransactions.last_page }}
                        </p>
                        <div class="flex gap-2">
                            <button
                                @click="cbPage--; fetch()"
                                :disabled="cbPage <= 1"
                                class="btn-action text-xs px-3 py-1.5 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Anterior
                            </button>
                            <button
                                @click="cbPage++; fetch()"
                                :disabled="cbPage >= cbTransactions.last_page"
                                class="btn-action text-xs px-3 py-1.5 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Próxima
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Timeline -->
                <div class="rounded-lg drop-shadow-sm overflow-hidden bg-white dark:bg-gray-800 p-6">
                    <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-4">Timeline</h3>

                    <div class="flow-root">
                        <ul role="list" class="-mb-8">
                            <li v-for="(interaction, idx) in review.interactions" :key="idx" class="relative pb-8">
                                <span
                                    v-if="idx !== review.interactions.length - 1"
                                    class="absolute left-4 top-8 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700"
                                />
                                <div class="relative flex items-start space-x-3">
                                    <div class="flex h-8 w-8 items-center justify-center rounded-full" :class="interactionIconClass(interaction)">
                                        <ChatBubbleLeftIcon v-if="interaction.interaction_type === 'comment'" class="h-4 w-4 text-white" />
                                        <BellIcon v-else-if="interaction.interaction_type === 'notification'" class="h-4 w-4 text-white" />
                                        <ArrowPathIcon v-else class="h-4 w-4 text-white" />
                                    </div>
                                    <div class="min-w-0 flex-1">
                                        <div class="flex items-center gap-2">
                                            <div v-if="interaction.user">
                                                <p class="text-sm font-medium text-gray-900 dark:text-white">{{ interaction.user.name }}</p>
                                                <p class="text-xs text-gray-400 dark:text-gray-500">{{ interaction.user.email }}</p>
                                            </div>
                                            <p v-else class="text-sm font-medium text-gray-400 dark:text-gray-500">Sistema</p>
                                            <span class="ml-auto text-xs text-gray-400 dark:text-gray-500">{{ interaction.timestamp }}</span>
                                        </div>
                                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">{{ interaction.description }}</p>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <!-- Comment Form -->
                    <div v-if="review.status === 'open'" class="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                        <textarea
                            v-model="commentText"
                            rows="3"
                            class="text-input"
                            placeholder="Adicionar comentário..."
                        ></textarea>
                        <div class="mt-2 flex justify-end">
                            <button
                                @click="submitComment"
                                :disabled="!commentText.trim() || submittingComment"
                                class="btn-primary-sm"
                            >
                                {{ submittingComment ? 'Enviando...' : 'Comentar' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sidebar (1 col) -->
            <div class="space-y-4">
                <!-- Assignee Card -->
                <div class="rounded-lg drop-shadow-sm bg-white dark:bg-gray-800 p-4 relative z-20">
                    <h4 class="text-xs font-bold text-gray-400 dark:text-gray-400 uppercase mb-3">Responsável</h4>
                    <div v-if="review.assignee_id" class="flex items-center justify-between">
                        <div>
                            <p class="text-sm font-medium text-gray-900 dark:text-white">{{ review.assignee_name }}</p>
                        </div>
                        <button
                            v-if="review.status === 'open'"
                            @click="review.assignee_id = null; review.assignee_name = null; assigneeSearch = ''; assigneeResults = [];"
                            class="text-xs text-indigo-500 hover:text-indigo-700 dark:text-indigo-400"
                        >Trocar</button>
                    </div>
                    <div v-if="!review.assignee_id && review.status === 'open'" class="relative">
                        <div class="flex gap-2">
                            <input
                                v-model="assigneeSearch"
                                @keyup.enter="searchAssignees"
                                type="text"
                                class="text-input-sm text-sm flex-1"
                                placeholder="Nome ou email..."
                                :disabled="updatingAssignee || assigneeLoading"
                            />
                            <button
                                @click="searchAssignees"
                                :disabled="assigneeSearch.length < 2 || assigneeLoading"
                                class="btn-primary-xs"
                            >
                                {{ assigneeLoading ? '...' : 'Buscar' }}
                            </button>
                        </div>
                        <p v-if="assigneeSearch.length > 0 && assigneeSearch.length < 2" class="text-xs text-gray-400 mt-1">Digite ao menos 2 caracteres</p>
                        <ul v-if="assigneeResults.length > 0" class="absolute z-50 mt-1 left-0 right-0 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-xl max-h-60 overflow-y-auto">
                            <li
                                v-for="user in assigneeResults"
                                :key="user.id"
                                @click="selectAssignee(user)"
                                class="px-3 py-2.5 cursor-pointer hover:bg-indigo-50 dark:hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-0"
                            >
                                <p class="text-sm font-medium text-gray-900 dark:text-white">{{ user.name }}</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400">{{ user.email }}</p>
                            </li>
                        </ul>
                        <p v-if="assigneeSearched && assigneeResults.length === 0" class="text-xs text-gray-400 mt-2">Nenhum usuário encontrado.</p>
                    </div>
                    <p v-if="!review.assignee_id && review.status !== 'open'" class="text-sm text-gray-400">Sem responsável</p>
                </div>

                <!-- Reference Month -->
                <div class="rounded-lg drop-shadow-sm overflow-hidden bg-white dark:bg-gray-800 p-4">
                    <h4 class="text-xs font-bold text-gray-400 dark:text-gray-400 uppercase mb-3">Mês de Referência</h4>
                    <p class="text-lg font-semibold text-gray-900 dark:text-white">{{ review.reference_month_display }}</p>
                    <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Pior mês nos últimos 3 meses</p>
                </div>

                <!-- Info Card -->
                <div class="rounded-lg drop-shadow-sm overflow-hidden bg-white dark:bg-gray-800 p-4">
                    <h4 class="text-xs font-bold text-gray-400 dark:text-gray-400 uppercase mb-3">Informações</h4>
                    <div class="space-y-3 text-sm">
                        <div>
                            <p class="text-xs text-gray-400 dark:text-gray-500">Aberto em</p>
                            <p class="text-gray-900 dark:text-white">{{ review.created_at }}</p>
                        </div>
                        <div v-if="review.closed_at">
                            <p class="text-xs text-gray-400 dark:text-gray-500">Fechado em</p>
                            <p class="text-gray-900 dark:text-white">{{ review.closed_at }}</p>
                        </div>
                        <div>
                            <p class="text-xs text-gray-400 dark:text-gray-500">Última interação</p>
                            <p v-if="review.last_interaction" class="text-gray-900 dark:text-white text-xs">
                                {{ review.last_interaction.timestamp }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Close Modal -->
        <div v-if="showCloseModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div class="rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800 max-w-md w-full">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">Encerrar Revisão</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    Tem certeza que deseja encerrar esta revisão? O status será alterado para "Fechado".
                </p>
                <div class="flex justify-end gap-3">
                    <button @click="showCloseModal = false" class="btn-action px-4 py-2 rounded-md text-sm font-medium">
                        Cancelar
                    </button>
                    <button
                        @click="closeReview"
                        :disabled="closing"
                        class="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {{ closing ? 'Encerrando...' : 'Confirmar' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BackButton from "../../../../components/layouts/navigation/BackButton.vue";
import TictoLoading from "../../../../components/layouts/TictoLoading.vue";
import StatCard from "../../../../components/layouts/StatCard.vue";
import { ChatBubbleLeftIcon, BellIcon, ArrowPathIcon, XCircleIcon, UserIcon } from "@heroicons/vue/24/outline";
import { useToast } from "vue-toastification";

export default {
    components: { BackButton, TictoLoading, StatCard, ChatBubbleLeftIcon, BellIcon, ArrowPathIcon, XCircleIcon, UserIcon },

    setup() {
        const toast = useToast();
        return { toast };
    },

    data() {
        return {
            review: null,
            loading: false,
            cbTransactions: { data: [], total: 0, current_page: 1, last_page: 1, from: 0, to: 0 },
            cbPage: 1,
            commentText: "",
            submittingComment: false,
            showCloseModal: false,
            closing: false,
            startingAudit: false,
            assigneeSearch: "",
            assigneeResults: [],
            assigneeLoading: false,
            assigneeSearched: false,
            updatingAssignee: false,
        };
    },

    computed: {
        statusClass() {
            if (this.review?.status === "open") return "bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/20 dark:text-green-400 dark:ring-green-500/30";
            return "bg-gray-50 text-gray-600 ring-gray-500/10 dark:bg-gray-700 dark:text-gray-400 dark:ring-gray-600";
        },
        rateTextClass() {
            if (this.review?.pct_chargeback > 2) return "text-red-600 dark:text-red-400";
            if (this.review?.pct_chargeback > 1.2) return "text-yellow-600 dark:text-yellow-400";
            return "text-green-600 dark:text-green-400";
        },
        tpvTextClass() {
            const rate = this.review?.tpv_rate || 0;
            if (rate > 2) return "text-red-600 dark:text-red-400";
            if (rate > 1.2) return "text-yellow-600 dark:text-yellow-400";
            return "text-green-600 dark:text-green-400";
        },
        chartOptions() {
            const progression = this.review?.monthly_progression || [];
            const categories = progression.map(m => {
                const [y, mo] = m.month.split('-');
                return `${mo}/${y}`;
            });

            return {
                credits: { enabled: false },
                title: { text: null },
                chart: { height: 280 },
                xAxis: {
                    categories,
                    crosshair: true,
                },
                yAxis: [
                    { title: { text: 'Quantidade' }, min: 0 },
                    {
                        title: { text: 'Taxa %' },
                        opposite: true,
                        min: 0,
                        labels: { format: '{value}%' },
                        plotLines: [{
                            value: 2,
                            color: '#ef4444',
                            dashStyle: 'Dash',
                            width: 1,
                            label: { text: 'Limite 2%', style: { color: '#ef4444', fontSize: '10px' } },
                        }],
                    },
                ],
                tooltip: { shared: true },
                series: [
                    {
                        name: 'Vendas',
                        type: 'column',
                        color: '#6366f1',
                        data: progression.map(m => parseInt(m.authorized) || 0),
                        yAxis: 0,
                    },
                    {
                        name: 'Chargebacks',
                        type: 'column',
                        color: '#ef4444',
                        data: progression.map(m => parseInt(m.chargebacks) || 0),
                        yAxis: 0,
                    },
                    {
                        name: 'TPN % (qtd)',
                        type: 'spline',
                        color: '#f59e0b',
                        data: progression.map(m => parseFloat(m.tpn_rate) || 0),
                        yAxis: 1,
                        tooltip: { valueSuffix: '%' },
                        marker: { enabled: true, radius: 4 },
                        lineWidth: 2,
                    },
                    {
                        name: 'TPV % (valor)',
                        type: 'spline',
                        color: '#8b5cf6',
                        dashStyle: 'ShortDash',
                        data: progression.map(m => parseFloat(m.tpv_rate) || 0),
                        yAxis: 1,
                        tooltip: { valueSuffix: '%' },
                        marker: { enabled: true, radius: 4 },
                        lineWidth: 2,
                    },
                ],
            };
        },
    },

    mounted() {
        this.fetch();
    },

    methods: {
        fetch() {
            this.loading = true;

            axios
                .get(`/api/chargeback-reviews/${this.$route.params.review_id}`, {
                    params: { cb_page: this.cbPage },
                })
                .then(({ data }) => {
                    this.review = data.data;
                    this.cbTransactions = data.data.chargeback_transactions || { data: [], total: 0, current_page: 1, last_page: 1, from: 0, to: 0 };
                    this.selectedAssignee = this.review.assignee_id;
                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                    this.toast.error("Erro ao carregar revisão.");
                });
        },

        searchAssignees() {
            if (this.assigneeSearch.length < 2) {
                return;
            }

            this.assigneeLoading = true;
            this.assigneeSearched = false;

            axios
                .get("/api/chargeback-reviews/search-assignees", { params: { q: this.assigneeSearch } })
                .then(({ data }) => {
                    this.assigneeResults = data;
                    this.assigneeLoading = false;
                    this.assigneeSearched = true;
                })
                .catch(() => {
                    this.assigneeLoading = false;
                });
        },

        selectAssignee(user) {
            this.assigneeResults = [];
            this.assigneeSearch = "";
            this.updatingAssignee = true;

            axios
                .post(`/api/chargeback-reviews/${this.review.id}/assignee`, { assignee_id: user.id })
                .then(({ data }) => {
                    this.review = data.data;
                    this.updatingAssignee = false;
                    this.toast.success("Responsável atualizado.");
                })
                .catch(() => {
                    this.updatingAssignee = false;
                    this.toast.error("Erro ao atualizar responsável.");
                });
        },

        startAudit() {
            this.startingAudit = true;

            axios
                .post("/api/audit", {
                    user_id: this.review.producer_id,
                    reason: "Blocked by chargeback score",
                    block_cashout: true,
                    block_products: true,
                })
                .then(({ data }) => {
                    this.startingAudit = false;
                    this.review.audit_id = data.data?.id || data.id;
                    this.review.audit_created_at = data.data?.created_at || data.created_at;
                    this.toast.success("Auditoria iniciada com sucesso.");
                })
                .catch((error) => {
                    this.startingAudit = false;
                    const msg = error.response?.data?.message || "Erro ao iniciar auditoria.";
                    this.toast.error(msg);
                });
        },

        submitComment() {
            if (!this.commentText.trim()) return;
            this.submittingComment = true;

            axios
                .post(`/api/chargeback-reviews/${this.review.id}/comment`, { description: this.commentText })
                .then(({ data }) => {
                    this.review = data.data;
                    this.commentText = "";
                    this.submittingComment = false;
                    this.toast.success("Comentário adicionado.");
                })
                .catch(() => {
                    this.submittingComment = false;
                    this.toast.error("Erro ao adicionar comentário.");
                });
        },


        closeReview() {
            this.closing = true;

            axios
                .post(`/api/chargeback-reviews/${this.review.id}/close`)
                .then(({ data }) => {
                    this.review = data.data;
                    this.showCloseModal = false;
                    this.closing = false;
                    this.toast.success("Revisão encerrada.");
                })
                .catch(() => {
                    this.closing = false;
                    this.toast.error("Erro ao encerrar revisão.");
                });
        },

        interactionIconClass(interaction) {
            if (interaction.interaction_type === "comment") return "bg-indigo-500";
            if (interaction.interaction_type === "stage_change") return "bg-gray-500";
            return "bg-amber-500";
        },

        formatCurrency(cents) {
            if (!cents && cents !== 0) return "R$ 0,00";
            return (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
        },

        statusLabel(status) {
            const labels = { chargeback: 'Chargeback', disputed: 'Disputado', chargeback_refunded: 'CB Reembolsado' };
            return labels[status] || status;
        },
    },
};
</script>
